import { useId, useLayoutEffect, useRef, useState, type CSSProperties, type JSX, type ReactNode } from "react";
import { COLOR, pct, type Provider } from "./data.js";

/* ------------------------------------------------------------------ icons (hand-drawn, no external assets) */

export type IconName = "home" | "usage" | "models" | "stats" | "settings" | "refresh" | "calendar" | "download" | "search" | "plus" | "sync" | "back" | "more";

const PATHS: Record<IconName, ReactNode> = {
  home: <path d="M3.5 9.2 10 3.8l6.5 5.4V16a.8.8 0 0 1-.8.8h-3.4v-4.4H7.7v4.4H4.3a.8.8 0 0 1-.8-.8Z" />,
  usage: <><path d="M3.6 10.2 10 4.6l6.4 5.6v5.6a.8.8 0 0 1-.8.8H4.4a.8.8 0 0 1-.8-.8Z" /><circle cx="10" cy="12.2" r="2.2" /></>,
  models: <path d="M3.5 16.5h13M5 16.5v-4.2M8.3 16.5V9.6M11.6 16.5V7.4M15 16.5V4.6M4.4 9.4l3.6-3 3.4 1.6 4.4-3.8" />,
  stats: <path d="M3.5 16.5h13M5.2 16.5v-5M8.6 16.5V8.2M12 16.5v-6.4M15.4 16.5V5.4M4.6 8.6 8.6 5l3.4 2.4 3.8-3.6" />,
  settings: <><rect x="3.6" y="4.2" width="12.8" height="11.6" rx="2.2" /><path d="M3.6 9.2h12.8M3.6 12.6h12.8M10 4.2v-1.4M10 17.2v-1.4" /></>,
  refresh: <><path d="M16 10a6 6 0 1 1-1.8-4.3" /><path d="M16 3.8v3.4h-3.4" /></>,
  sync: <><circle cx="10" cy="10" r="6.6" /><path d="M7 9.2a3.2 3.2 0 0 1 5.7-1.2M13 10.8a3.2 3.2 0 0 1-5.7 1.2M12.9 6.4v1.8h-1.8M7.1 13.6v-1.8h1.8" /></>,
  calendar: <><rect x="3.5" y="4.5" width="13" height="12" rx="2" /><path d="M3.5 8.4h13M7 3v3M13 3v3M8.6 11.4h2.8v2.8H8.6z" /></>,
  download: <path d="M10 3.6v8.6M6.6 9l3.4 3.4L13.4 9M4 15.8h12" />,
  search: <><circle cx="9" cy="9" r="5" /><path d="m12.8 12.8 3.6 3.6" /></>,
  plus: <path d="M10 4v12M4 10h12" />,
  back: <path d="m12 4-6 6 6 6" />,
  more: <><circle cx="5" cy="10" r=".9" /><circle cx="10" cy="10" r=".9" /><circle cx="15" cy="10" r=".9" /></>,
};

export function Icon({ name, className }: { name: IconName; className?: string }): JSX.Element {
  return <svg className={`cx-icon ${className ?? ""}`} viewBox="0 0 20 20" aria-hidden="true">{PATHS[name]}</svg>;
}

/** Vendor mark: outlined triangle with an inner notch, red for Claude, blue for Codex. */
export function Logo({ provider, size = 22 }: { provider: Provider; size?: number }): JSX.Element {
  const id = useId();
  const [from, to] = provider === "claude" ? ["#ff7a52", "#ff2d3d"] : ["#5fc8ff", "#1f6bff"];
  return (
    <svg className={`cx-logo ${provider}`} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <defs><linearGradient id={id} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={from} /><stop offset="1" stopColor={to} /></linearGradient></defs>
      <path d="M12 2.6 22.2 20.4H1.8Z" fill="none" stroke={`url(#${id})`} strokeWidth="3.1" strokeLinejoin="round" />
      <path d="M12 11.2 15.6 17.4H8.4Z" fill={`url(#${id})`} />
    </svg>
  );
}

/* ------------------------------------------------------------------ small parts */

export type PillTone = "ok" | "warn" | "idle" | "bad";
export function Pill({ tone, children }: { tone: PillTone; children: ReactNode }): JSX.Element {
  return <span className={`cx-pill ${tone}`}><i />{children}</span>;
}

export function Seg<T extends string | number>({ items, value, onChange }: { items: ReadonlyArray<readonly [T, string]>; value: T; onChange: (value: T) => void }): JSX.Element {
  return (
    <div className="cx-seg" role="tablist">
      {items.map(([key, label]) => <button key={String(key)} role="tab" aria-selected={value === key} className={value === key ? "on" : ""} onClick={() => onChange(key)}>{label}</button>)}
    </div>
  );
}

export function DatePill({ text }: { text: string }): JSX.Element {
  return <span className="cx-datepill"><span>{text}</span><Icon name="calendar" /></span>;
}

export function Ring({ value, label, provider, size = 108, stroke = 11 }: { value: number | null; label: string; provider: Provider; size?: number; stroke?: number }): JSX.Element {
  const id = useId();
  const safe = Math.max(0, Math.min(100, value ?? 0));
  const radius = 50 - stroke / 2 - 2;
  const circumference = 2 * Math.PI * radius;
  const [from, to] = provider === "claude" ? ["#ff6b74", "#e0101e"] : ["#62a8ff", "#1b5fe6"];
  return (
    <div className={`cx-ring ${provider}`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" aria-label={label} role="img">
        <defs><linearGradient id={id} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={from} /><stop offset="1" stopColor={to} /></linearGradient></defs>
        <circle className="track" cx="50" cy="50" r={radius} fill="none" strokeWidth={stroke} />
        {value !== null && safe > 0 && <circle className="arc" cx="50" cy="50" r={radius} fill="none" stroke={`url(#${id})`} strokeWidth={stroke} strokeDasharray={`${(circumference * safe) / 100} ${circumference}`} transform="rotate(-90 50 50)" />}
      </svg>
      <div className="cx-ring-in">
        <b>{value === null ? "--" : <>{Math.round(safe)}<small>%</small></>}</b>
        <span>{label}</span>
      </div>
    </div>
  );
}

export function Meter({ value, provider, className }: { value: number | null; provider: Provider; className?: string }): JSX.Element {
  const safe = Math.max(0, Math.min(100, value ?? 0));
  return <div className={`cx-meter ${provider} ${className ?? ""}`}><i style={{ width: value === null ? "5%" : safe > 0 ? `${Math.max(safe, 3)}%` : "0", opacity: value === null ? 0.55 : 1 }} /></div>;
}

export function QuotaRow({ label, value, provider }: { label: string; value: number | null; provider: Provider }): JSX.Element {
  return (
    <div className="cx-qrow">
      <span>{label}</span>
      <div><Meter value={value} provider={provider} /><b>{pct(value)}</b></div>
    </div>
  );
}

/**
 * Single-line number that shrinks its font until it fits its box; when even the minimum size overflows it switches
 * to the compact fallback text (e.g. 199,290,623 → 199.29M). Width comes from the parent, never from the text.
 */
export function Fit({ children, fallback, max, min = 10, className, style }: { children: ReactNode; fallback?: ReactNode; max: number; min?: number; className?: string; style?: CSSProperties }): JSX.Element {
  const ref = useRef<HTMLSpanElement>(null);
  const [alt, setAlt] = useState(false);
  const signature = `${String(max)}:${String(min)}:${alt ? "1" : "0"}`;
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const fit = (): void => {
      let size = max;
      element.style.fontSize = `${size}px`;
      while (element.scrollWidth > element.clientWidth + 0.5 && size > min) { size -= 0.5; element.style.fontSize = `${size}px`; }
      if (!alt && fallback !== undefined && element.scrollWidth > element.clientWidth + 0.5) setAlt(true);
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    void document.fonts?.ready.then(fit);
    return () => observer.disconnect();
  });
  return <span ref={ref} data-fit={signature} className={`cx-fit ${className ?? ""}`} style={style}>{alt && fallback !== undefined ? fallback : children}</span>;
}

/** Rough rendered width (px) of a string: CJK glyphs are 1em, digits ~0.6em, separators narrower. */
export function textWidth(text: string, px: number): number {
  let width = 0;
  for (const char of text) width += /[\u2e80-\u9fff\uff00-\uffef]/.test(char) ? px * 1.08 : /[.,:\s]/.test(char) ? px * 0.3 : /[A-Z$%]/.test(char) ? px * 0.66 : px * 0.58;
  return width;
}

/** Flex basis for a label-over-value tile so tiles share a row in proportion to what they must show. */
export const tileBasis = (label: string, value: string, labelPx: number, valuePx: number, padding: number): string => `1 1 ${Math.ceil(Math.max(textWidth(label, labelPx), textWidth(value, valuePx)) + padding)}px`;

/** Money with a smaller cents part, as on the detail comp ($14,396.95). */
export function Cents({ text }: { text: string }): JSX.Element {
  const dot = text.lastIndexOf(".");
  if (dot < 0) return <>{text}</>;
  return <>{text.slice(0, dot)}<small className="cents">{text.slice(dot)}</small></>;
}

export function PageHead({ title, subtitle, right, onBack }: { title: string; subtitle: string; right?: ReactNode; onBack?: () => void }): JSX.Element {
  return (
    <header className="cx-head">
      <div className="cx-title">
        <h1>{onBack && <button className="cx-back" aria-label="返回" onClick={onBack}><Icon name="back" /></button>}{title}</h1>
        <p>{subtitle}</p>
      </div>
      {right && <div className="cx-head-right">{right}</div>}
    </header>
  );
}

export const providerGradient = (provider: Provider): [string, string] => provider === "claude" ? ["#ff6a72", "#c4141f"] : ["#5aa2ff", "#1546c8"];
export const accent = (provider: Provider): string => COLOR[provider];
